import * as arr from "./helpers/array";
import {getSheetData} from "./operations/data";
import {DataStore} from "@xbs/muon";
import {loadCell} from "./operations/types";
import {removeUnusedStyles, styleFromText, styleToText} from "./operations/styles";
import {getFormatFromData, checkFormat, removeUnusedFormats, clearFormatCache, updateFormats} from "./operations/formats";
import {updateMaxColumn, encode, escapeSheet, toRange, range} from "./helpers/column_names";

const options = [
	"rename-sheet",
	"remove-sheet",
	"copy-sheet",
	"hide-sheet"
];

const actionMap = {
	"remove-sheet": view => _remove(view),
	"rename-sheet": view => edit(view),
	"new-sheet": view => add(view),
	"copy-sheet": view => copy(view),
	"hide-sheet": view => setState(view, view._activeSheet, "hidden")
};

export function init(view){
	view.attachEvent("onComponentInit", () => ready(view));
}

export const numbers = [0.5, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 3, 4];

export function defaultBottom(view){
	const locale = webix.i18n.spreadsheet.tooltips;
	const readonly = view.config.readonly;
	const tabWidth = view.config.sheetTabWidth;
	const minTabWidth = tabWidth == "auto" ? view.defaults.sheetTabWidth : tabWidth;

	const toolbar =  {
		view: "toolbar",
		id: "bottom-toolbar",
		css: "webix_ssheet_bottom_toolbar webix_layout_toolbar",
		paddingY: 0,
		height: 34,
		elements:[
			{
				hidden:readonly,
				padding: {
					top: 2,
					bottom: 2,
					left: 4
				},
				cols:[
					{
						view:"icon", 
						icon:"wxi-plus", 
						width:30, 
						id: "add-sheet", 
						click: () => add(view),
						tooltip: locale["add-sheet"]
					},
					{
						view:"icon", 
						icon:"wxi-dots", 
						width:30, 
						id: "show-sheet", 
						popup: getSheetsPopup(view),
						tooltip: locale["show-sheet"]
					}
				]
			},
			{
				id:"sheets",
				view:"sheets",
				maxWidth: 5 * minTabWidth,
				minWidth: minTabWidth,
				editable:!readonly,
				editaction: "dblclick",
				editor:"text",
				editValue:"value",
				type:{
					width: tabWidth,
					height: 32,
					template: function(obj){
						return "<div style='"+(readonly ? "width: 100%;padding-right:9px;" : "width:calc(100% - 20px);")+"'>"+obj.value+"</div>"+
							(!readonly?"<div class='webix_input_icon wxi-menu-down webix_ssheet_sheet_arrow'></div>":"");
					},
					//remove skin selection css on dnd
					css:"ssheet_order_sheets"
				},
				onClick:{
					webix_ssheet_sheet_arrow: function(e, id){
						initSheetMenu(view, e, id);
					}
				},
				on:{
					onItemClick: function(id){
						if(id != view._activeSheet)
							view.showSheet(id);
					}
				}
			},
			{
				view: "button", width: 34, id: "prev-sheet", disabled:true, hidden:true,
				label: "<span class='webix_icon wxi-menu-left'></span>", batch:"pager",
				click:function(){scrollSheets(view, -1);}
			},
			{
				view: "button", width: 34, id: "next-sheet", hidden:true,
				label: "<span class='webix_icon wxi-menu-right'></span>", batch:"pager",
				click:function(){scrollSheets(view, 1);}
			},
			{gravity:0.000001},
			{
				view: "richselect",
				css: "webix_ssheet_stats",
				id: "stats",
				width: 200,
				value: "sum",
				options: {
					view:"suggest",
					padding: 0,
					css: "webix_ssheet_suggest webix_ssheet_stats_suggest",
					body: {
						template: obj => {
							return `<span class="webix_ssheet_stats_name">${webix.i18n.spreadsheet.labels[obj.id]}:</span> ${obj.value}`;
						},
						data: getStats(view)
					}
				}
			},
			{
				view: "ssheet-zoom",
				numbers,
				value: 1,
				width: 108,
				tooltip: locale["scale"],
				id: "zoom",
				on: {
					onChange: (v, oldV, config) => {
						if (config == "user") view.zoom(v);
					}
				},
			}
		]
	};

	view.callEvent("onViewInit", ["bottom-toolbar", toolbar]);

	return toolbar;
}

function getStats(view){
	return ["sum", "average", "min", "max", "counta", "count"]
		.map(id => {
			let value;
			if(view._mPage){
				const sel = view.getSelectedRange();
				if(sel)
					value = view._mData.getStore().exec(`${id}(${sel})`, view._mPage.getContext());
			}
			if(!value || value == "#ERROR")
				value = 0;
			value = Math.round(value * 100) / 100;
			return { id, value };
		});
}

function scrollSheets(view, direction){
	const sheets = view.$$("sheets");
	let step = view.config.sheetTabWidth;
	if(step == "auto")
		step = view.defaults.sheetTabWidth;
	sheets.scrollTo(sheets.getScrollState().x+(direction*step), 0);
}

function menuInit(view){
	const data = [];
	for (let i = 0; i < options.length; i++) {
		data.push({
			id: options[i], 
			value: webix.i18n.spreadsheet.menus[options[i]] || options[i]
		});
	}

	const menu = {
		view: "ssheet-suggest",
		data: data
	};
	view.callEvent("onViewInit", ["sheet-menu", menu]);
	return menu;
}

export function reset(view, obj){
	const tabs = view.$$("sheets");
	if (tabs){
		tabs.clearAll();
		const set = [];
		obj.sheets.forEach(function(sheet){
			set.push({id:sheet.name, value: sheet.name});
		});
		tabs.parse(set);
		_filterTabs(view);
	}
}

function ready(view){
	prepare(view);

	view.attachEvent("onAfterSheetShow", (name) => selectSheet(view,name));

	view.attachEvent("onCommand", function(command){
		if(actionMap[command.id])
			actionMap[command.id](this);
	});

	const sheets = view.$$("sheets");
	if(sheets){
		sheets.data.attachEvent("onStoreUpdated", () => {
			changePagerVisability(view);
			changePagerStatus(view);
		});
		sheets.attachEvent("onAfterRender", webix.once(() => {
			webix.delay(()=> changePagerVisability(view));
		}));
		sheets.attachEvent("onAfterScroll", () => changePagerStatus(view));
		sheets.attachEvent("onAfterDrop", (context) => dataOrder(view,context.start,context.index));
		sheets.attachEvent("onBeforeEditStop", (state) => rename(view,state.old,state.value,true));
	}

	const stats = view.$$("stats");
	if(stats){
		const grid = view.$$("cells");
		let alreadyUpdates;
		view.attachEvent("onCellChange", (r, c)=> {
			if(!alreadyUpdates){
				const sel = grid.getSelectArea();
				if(sel && r >= sel.start.row && r <= sel.end.row && c >= sel.start.column && c <= sel.end.column){
					alreadyUpdates = webix.delay(()=>{
						stats.getList().parse(getStats(view));
						alreadyUpdates = null;
					});
				}
			}
		});
		grid.attachEvent("onSelectChange", () => stats.getList().parse(getStats(view)));
	}
}

function getSheetsPopup(view){
	return {
		view: "popup",
		css: "webix_ssheet_suggest",
		padding: 0,
		width: 125,
		body:{
			view: "list",
			select: true,
			css: "webix_ssheet_show_list",
			template: "<span class='webix_icon wxi-#icon#'></span>#id#",
			on:{
				onAfterSelect: function(id) {
					setState(view, id, "visible");
					view.showSheet(id);
					this.getParentView().hide();
				}
			},
			maxHeight: 200,
			autoheight: true
		},
		on:{
			onShow: function(){
				this.getBody().showItem(view.getActiveSheet());
			},
			onBeforeShow: function(){
				const data = view._sheets
					.filter(sheet => sheet.state != "veryHidden")
					.map(sheet => {
						return {
							id: sheet.name,
							icon: sheet.state == "hidden" ? "eye-slash" : "eye"
						};
					});

				const list = this.getBody();
				list.clearAll();
				list.parse(data);
				list.select(view.getActiveSheet());
			}
		}
	};
}

function initSheetMenu(view, e, id){
	if(!view.$sheetMenu){
		view.$sheetMenu = webix.ui(menuInit(view));
		view._destroy_with_me.push(view.$sheetMenu);
		view.$sheetMenu.attachEvent("onItemClick", id => callAction(view,id));
	}

	const oneSheet = view.$$("sheets").count() == 1;

	if(view.$sheetMenu.getItem("remove-sheet"))
		view.$sheetMenu[oneSheet ? "disableItem" : "enableItem"]("remove-sheet");

	if(view.$sheetMenu.getItem("hide-sheet"))
		view.$sheetMenu[oneSheet ? "disableItem" : "enableItem"]("hide-sheet");

	if(view.callEvent("onBeforeSheetMenu", [id]))
		view.$sheetMenu.show(e);
}

function changePagerStatus(view){
	const sheets = view.$$("sheets");
	const scrollPos = sheets.getScrollState().x;
	if(scrollPos == 0)
		view.$$("prev-sheet").disable();
	else{
		view.$$("prev-sheet").enable();
		if(sheets.$view.scrollWidth - scrollPos == sheets.$width)
			return view.$$("next-sheet").disable();
	}
	view.$$("next-sheet").enable();
}

function changePagerVisability(view){
	const sheets = view.$$("sheets");
	view.$$("bottom-toolbar").showBatch("pager", sheets.$width != sheets.$view.scrollWidth);
}

function dataOrder(view, name, num){
	for (let id in view._sheets) {
		if (view._sheets[id].name == name) {
			const item = view._sheets.splice(id, 1);
			view._sheets.splice(num, 0, item[0]);
			break;
		}
	}
}

export function setState(view, sheet, state){
	const tab = getSheet(view, sheet);

	if(state == tab.state || (state == "visible" && !tab.state))
		return;

	if(state != "visible"){
		const visibleSheets = getVisible(view);

		//don't hide last visible tab
		if(visibleSheets.length == 1 && visibleSheets[0] === tab)
			return;

		//select the previous sheet in case we hide the selected sheet
		if(sheet == view._activeSheet)
			showPrevious(view, sheet);
	}

	tab.state = state;
	_filterTabs(view);

	view.callEvent("onSheetStateChange", [sheet, state]);
}

function _filterTabs(view){
	const tabs = view.$$("sheets");
	if(tabs)
		tabs.filter(obj => {
			const state = getSheet(view, obj.id).state;
			return state != "hidden" && state != "veryHidden";
		});
}

export function add(view, content, name, show){
	var index, newIndex;

	index = arr.findIndex(view._sheets, x => x.name == view._activeSheet);
	newIndex = index>-1?index+1:view._sheets.length;

	if(!name || arr.find(view._sheets, sheet => sheet.name == name))
		name = getNewTitle(view);

	content = content || {data:[]};
	view._sheets.splice(newIndex, 0, {
		name,
		content
	});

	if(view.$$("sheets"))
		view.$$("sheets").add({id: name, value: name},newIndex);

	view._mData.addPage(name);

	parseToStore(view, name, content, view.config.prepareData);

	if(show !== false)
		view.showSheet(name);
	view.callEvent("onSheetAdd", [name]);

	return name;
}

function getNewTitle(view){
	var index = view._sheets.length+1;
	while(getSheet(view, (webix.i18n.spreadsheet.labels.sheet+index)))
		index++;
	var name = webix.i18n.spreadsheet.labels.sheet+index;
	return name;
}

function selectSheet(view,name){
	if(view.$$("sheets")){
		view.$$("sheets").select(name);
		view.$$("sheets").showItem(name);
	}
}

export function edit(view,name){
	if(!name)
		name = view._activeSheet;
	view.$$("sheets").edit(name);
}

function _remove(view){
	view.confirm({
		text: webix.i18n.spreadsheet.labels["sheet-remove-confirm"]
	}).then(()=>{
		remove(view, view._activeSheet);
	});
}

function getVisible(view){
	return view._sheets.filter(s => s.state != "hidden" && s.state != "veryHidden");
}

function showPrevious(view, name){
	const visibleSheets = getVisible(view);

	let index = arr.findIndex(visibleSheets, x => x.name == name);
			
	let prevIndex = index - 1;
	if(!visibleSheets[prevIndex])
		prevIndex = 0;
	visibleSheets.splice(index, 1);

	view.showSheet(visibleSheets[prevIndex].name);
}

export function remove(view, name){
	if(view._sheets.length > 1){
		const visibleSheets = getVisible(view);

		//don't remove last visible tab
		if(visibleSheets.length == 1 && visibleSheets[0] === getSheet(view, name))
			return;

		//select the previous sheet in case we remove the selected sheet
		if(view._activeSheet == name)
			showPrevious(view, name);

		const index = arr.findIndex(view._sheets, x => x.name == name);
		view._sheets.splice(index, 1);
		view._mData.removePage(name);

		if(view.$$("sheets")){
			view.$$("sheets").remove(name);
			view.$$("sheets").refresh();
		}

		view.callEvent("onSheetRemove", [name]);
	}
}

export function rename(view, name, newName, user){
	if(name == newName) return true;
	const locale = webix.i18n.spreadsheet.labels;

	// avoid multiple rename error wins
	if(view.$renameError) return false;

	if(getSheet(view, newName)){
		view.$renameError = view.alert({
			title: locale["sheet-rename-invalid-name"],
			css: "webix_ssheet_invalid_sheet_name",
			text: locale["sheet-rename-name-exists"],
			width: "auto",
			type: "alert-error"
		}).then(()=>{
			delete view.$renameError;
			if(user)
				view.$$("sheets").getEditor().getInputNode().focus();
		});
		return false;
	}

	if(
		!newName ||
		newName.length > 31 ||
		/[*?:[\]\\/]/g.test(newName)
	){
		view.$renameError = view.alert({
			title: locale["sheet-rename-invalid-name"],
			css: "webix_ssheet_invalid_sheet_name",
			text: `
				<ul>
					<li>${locale["sheet-rename-no-blank"]}</li>
					<li>${locale["sheet-rename-name-length"]}</li>
					<li>${locale["sheet-rename-invalid-characters"]}</li>
				</ul>`,
			width: "auto",
			type: "alert-error"
		}).then(()=>{
			delete view.$renameError;
			if(user)
				view.$$("sheets").getEditor().getInputNode().focus();
		});
		return false;
	}

	const sheet = getSheet(view, name);
	const sheets = view.$$("sheets");

	sheet.name = newName;
	if(view._activeSheet == name)
		view._activeSheet = newName;

	if(sheets){
		const item = sheets.getItem(name);
		item.value = newName;
		sheets.data.changeId(name,newName);
		sheets.refresh(newName);
	}

	view._mData.renamePage(name, newName);
	view.callEvent("onSheetRename", [name, newName]);
}

export function serialize(view, obj, config){
	const active = getSheet(view, view._activeSheet);
	active.content = obj;
	active.state = active.state || "visible";

	const sheets = webix.copy(view._sheets);

	const math = !config || config.math!==false;
	const compactStyles = config && config.compactStyles;

	sheets.forEach(sheet => {
		if(sheet.name != view._activeSheet){
			[sheet.content.data, sheet.content.locked] = getSheetData(view, math, sheet.name);
			sheet.state = sheet.state || "visible";
			if(compactStyles)
				sheet.content.styles = removeUnusedStyles(sheet.content, config._usedFormats);
			sheet.content.ranges = view.ranges.serialize(sheet.name);
		}
	});

	const newFormats = removeUnusedFormats(obj.formats, config._usedFormats);
	if(newFormats)
		sheets.forEach(sheet => sheet.content.formats = newFormats);

	return sheets;
}

function parseToStore(view, name, obj, prepareData){
	const ranges = obj.ranges;

	if(prepareData && ranges){
		const filtersIndex = arr.findIndex(ranges, range => range[0] == "_XLNM._FILTERDATABASE");
		if(filtersIndex != -1){
			if(!obj.filters) obj.filters = [];

			const rangeString = ranges[filtersIndex][1];
			if(rangeString){
				const [r1, c1, r2, c2] = range(rangeString);
				for(let c = c1; c <= c2; c++){
					obj.filters.push([
						r1,
						c,
						{options: toRange(r1+1, c, r2, c), lastRow: r2}
					]);
				}
				ranges.splice(filtersIndex, 1);
			}
		}
	}

	view.ranges.parse(ranges, name);

	webix.extend(obj, {
		styles: [],
		formats: [],
		editors: []
	});

	const page = view._mData.getPage(name);
	const data = obj.data.map(arr => {
		const invalidValue = webix.isUndefined(arr[2]) || arr[2] === null;

		if(prepareData){
			if(invalidValue)
				arr[2] = "";
			else {
				const linkRegex = /^=link\(/i;
				if(typeof arr[2] == "string" && linkRegex.test(arr[2])){
					// LINK is deprecated
					arr[2] = arr[2].replace(linkRegex, "=HYPERLINK(");
				}
			}

			const cell = loadCell(view, obj, arr);
			//cell value and type can be changed via types module
			arr[2] = cell.value;
			arr[3] = arr[3] || "";
			arr[4] = cell.type;

			prepareCell(view, arr, obj);
		}
		else if(invalidValue)
			throw `unsupported value in ${escapeSheet(name)}!${encode[arr[0]]}${arr[1]}`;

		return [ arr[0]-1, arr[1]-1, {
			value: arr[2],
			type: arr[4],
			style: arr[3],
			$format: checkFormat(view, { fmt: getFormatFromData(view, obj, arr) }, "string") ? 1 : 0
		}];
	});

	page.parse(data, true);
}

export function prepareCell(view, cell, data, update){
	let lastFixedStyle = Math.max(...data.styles.map(arr => arr[0].startsWith("wss_fixed_") ? arr[0].replace("wss_fixed_", "")*1 : 0));
	const props = {};

	const styleProperties = cell[3] ? arr.find(data.styles, style => style[0] == cell[3]) : null;
	const styleObj = styleProperties ? styleFromText(styleProperties[1]) : {};

	const isDate = cell[4] == "date";
	if(isDate || update){
		const isFormula = typeof cell[2] == "string" && cell[2][0] == "=" && cell[2].length > 1;

		const updateLink = update && isFormula && /^=hyperlink\(/i.test(cell[2]);
		const editor = isDate && data.editors ? arr.find(data.editors, editor => editor[0] == cell[0] && editor[1] == cell[1]) : null;

		if((isDate && !(styleObj.format && editor)) || updateLink){
			if(updateLink)
				props = { color: "#0000ff", underline: "underline" };
			else if(!styleObj.format){
				if(isFormula && /^=time\(/i.test(cell[2])){
					props.format = cell[3] ? cell[3]+"_fmt" : "wss_time_fmt";
					data.formats.push([props.format, webix.i18n.spreadsheet.formats.timeFormat]);
				}
				else
					props.format = "date";
			}

			if(!isFormula && isDate && !editor){
				if(!data.editors)
					data.editors = [];
				data.editors.push([cell[0], cell[1], {editor: "excel_date"}]);
			}
		}
	}

	if(view.config.borderCollapse && !update){
		if(styleObj["border-left"]){
			const leftCell = data.data.find(item => cell[0] == item[0] && cell[1]-1 == item[1]);
			if(leftCell && leftCell[3]){
				const leftStyle = data.styles.find(arr => arr[0] == leftCell[3])[1].split(";");
				if(leftStyle[12]) // right border
					props["border-left"] = "";
			}
		}
		if(styleObj["border-top"]){
			const topCell = data.data.find(item => cell[0]-1 == item[0] && cell[1] == item[1]);
			if(topCell && topCell[3]){
				const topStyle = data.styles.find(arr => arr[0] == topCell[3])[1].split(";");
				if(topStyle[13]) // bottom border
					props["border-top"] = "";
			}
		}
	}

	if(Object.keys(props).length){
		const newStyleText = styleToText({props: webix.extend(styleObj, props, true)});
		let style = arr.find(data.styles, style => style[1] == newStyleText);
		if(!style){
			cell[3] = "wss_fixed_"+(++lastFixedStyle);
			data.styles.push([cell[3], newStyleText]);
		}
		else
			cell[3] = style[0];
	}
}

export function load(view, obj, excel){
	view._loading_data = true;
	obj = webix.isArray(obj)?{sheets:obj}:obj;
	const prepareData = view.config.prepareData || excel;

	if(!obj.sheets && view._activeSheet){
		if(view._sheets.length == 1)
			clearFormatCache(view);
		else{
			updateFormats(view, obj);
		}
		//loading into active sheet
		const name = view._activeSheet;
		getSheet(view, name).content = obj;
		parseToStore(view, name, obj, prepareData);
		refresh(view, obj);
	} else {
		clearFormatCache(view);
		//creating new set of sheets
		prepare(view, obj, prepareData);
	}
	view._loading_data = false;
}

export function prepare(view, obj, prepareData){
	if(!obj) obj = {data:[]}; 
	if(!obj.sheets){
		obj = newSheet(obj);
	}

	view._activeSheet = "";
	view._sheets = obj.sheets;
	view._mData = new DataStore({strict: view.config.strict});

	// first add all pages then parse data in them
	obj.sheets.forEach(sheet => {
		view._mData.addPage(sheet.name);
	});

	obj.sheets.forEach(sheet => {
		const content = sheet.content;
		const sheetName = sheet.name;

		parseToStore(view, sheetName, content, prepareData);
	});

	reset(view, obj);
	show(view, getVisible(view)[0].name);
}

export function newSheet(obj){
	return {
		sheets:[{
			name: webix.i18n.spreadsheet.labels.sheet + 1,
			content: obj
		}]
	};
}

export function show(view, name){
	if (name == view._activeSheet) return;

	if(view.callEvent("onBeforeSheetShow", [name])){
		if (view._activeSheet)
			getSheet(view, view._activeSheet).content = view.serialize();

		view._activeSheet = name;
		const obj = getSheet(view, name).content;

		view._mPage = view._mData.getPage(name);

		refresh(view, obj);

		view.callEvent("onAfterSheetShow", [name]);
	}
}

export function getSheet(view, name){
	return arr.find(view._sheets, x => x.name == name);
}

export function isVisible(view, sheet){
	sheet = getSheet(view, sheet);
	return sheet.state != "hidden" && sheet.state != "veryHidden";
}

export function eachSheet(view, callback){
	for (var i=0; i<view._sheets.length; i++)
		callback(view._sheets[i].name);
}

export function getActive(view){
	return view._activeSheet;
}

function refresh(view, obj){
	const [data] = getSheetData(view);

	let [rows, cols] = arr.getDimension(data, obj.spans, view.config.rowCount, view.config.columnCount, obj.filters);

	if (rows != view.config.rowCount || cols != view.config.columnCount){
		view.config.rowCount = rows;
		view.config.columnCount = cols;
		view._resetTable();
	}

	updateMaxColumn(cols);

	view._loading_data = true;
	view.callEvent("onReset", []);
	view.callEvent("onDataParse", [obj]);
	view._loading_data = false;

	view._table.refresh();
	if(view.config.bottombar)
		view.$$("sheets").refresh();
}

function copy(view){
	add(view, view.serialize());
}

function callAction(view, id){
	if(!view.$sheetMenu.data.getMark(id, "webix_disabled")){
		if(actionMap[id])
			actionMap[id](view);
		else
			view.callEvent("onCommand", [{id}]);
	}
}

